import React from 'react';
import { useTranslation } from 'react-i18next';
import { DotsMenu12, Lock12, SlimArrowUp } from 'shared/assets/icons';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors as IconButtonColors,
  IconButton,
  Sizes,
} from 'shared/components/ui/icon-button';
import { TimeProgressChart } from 'shared/components/ui/time-progress-chart';
import { DateEntry } from 'shared/lib/recharts';
import { Goal } from 'shared/types/goal';
import { metricUnitFormatBase } from 'shared/types/metric-template';
import { NumberFormat } from 'shared/types/number-format-options';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { formatNumberShort } from 'shared/utils/format-number-short';
import { isNumber } from 'shared/utils/is-number';

import * as Styled from './summary-progress-card.style';

export type SummaryProgressCardProps = {
  goal: Goal;
  entries: DateEntry[];
  completionPercentage?: number;
  unitFormat?: string;
  startValue?: number;
  currentValue?: number;
  target?: number;
  timeframeDifference?: number;
  startDate: Timestamp;
  endDate: Timestamp;
  weekStartsOn: WeekDays;
  numberFormat?: NumberFormat;
  onPremium: () => void;
  menuButtonRef?: React.RefObject<HTMLButtonElement>;
  onMenuButton?: (e: React.MouseEvent) => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  onEditGoalProgress?: (e: React.MouseEvent) => void;
  isEmpty?: boolean;
  isLoading?: boolean;
};

export const SummaryProgressCard: React.FC<SummaryProgressCardProps> = ({
  goal,
  entries,
  completionPercentage,
  unitFormat,
  startValue,
  currentValue,
  target,
  timeframeDifference,
  startDate,
  endDate,
  weekStartsOn,
  numberFormat,
  onPremium,
  menuButtonRef,
  onMenuButton,
  onContextMenu,
  onEditGoalProgress,
  isEmpty,
  isLoading,
}) => {
  const { t } = useTranslation();

  const lastEntryValue =
    currentValue ?? entries?.[entries.length - 1]?.value ?? startValue;
  const formattedTarget = isNumber(target)
    ? formatNumberShort(target, numberFormat?.decimal)
    : undefined;
  const formattedLastEntryValue = formatNumberShort(
    lastEntryValue ?? 0,
    numberFormat?.decimal,
  );

  const differenceIsPositive =
    isNumber(startValue) && isNumber(target) && startValue > target
      ? (timeframeDifference ?? 0) < 0
      : (timeframeDifference ?? 0) > 0;

  const onMenuButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onMenuButton?.(e);
  };

  const onUpdateClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onEditGoalProgress?.(e);
  };

  return (
    <Styled.Container
      onClick={goal.frozenAt ? onPremium : undefined}
      onContextMenu={goal.frozenAt ? undefined : onContextMenu}
    >
      {isLoading ? (
        <Styled.LoadingBody />
      ) : (
        <Styled.Body $isFrozen={!!goal.frozenAt}>
          <Styled.CurrentProgress>
            {!!unitFormat ? (
              <>
                <Styled.CurrentProgressValue
                  as={onEditGoalProgress ? 'button' : undefined}
                  type={onEditGoalProgress ? 'button' : undefined}
                  $isEditable={!!onEditGoalProgress}
                  $isEmpty={!!isEmpty}
                  onClick={onEditGoalProgress ? onUpdateClick : undefined}
                >
                  {formattedTarget
                    ? formattedLastEntryValue
                    : unitFormat?.replace(
                        metricUnitFormatBase,
                        formattedLastEntryValue,
                      )}
                </Styled.CurrentProgressValue>
                {formattedTarget ? (
                  <Styled.CurrentProgressTarget>
                    <Styled.CurrentProgressSlash />
                    {unitFormat?.replace(metricUnitFormatBase, formattedTarget)}
                  </Styled.CurrentProgressTarget>
                ) : null}
              </>
            ) : (
              <Styled.NoTasksLabel>
                {t('general.labels.insights-card.no-tasks.label')}
              </Styled.NoTasksLabel>
            )}

            <Styled.IconButtonContainer>
              <IconButton
                icon={goal.frozenAt ? Lock12 : DotsMenu12}
                onClick={goal.frozenAt ? undefined : onMenuButtonClick}
                size={Sizes.ExtraSmall}
                color={IconButtonColors.Default}
                ref={menuButtonRef}
              />
            </Styled.IconButtonContainer>
          </Styled.CurrentProgress>

          <Styled.Progress>
            <CircleProgress percentage={completionPercentage} isSmall />

            {!!timeframeDifference && (
              <Styled.Difference $positive={differenceIsPositive}>
                <Styled.DifferenceIconContainer $up={timeframeDifference > 0}>
                  <Icon icon={SlimArrowUp} />
                </Styled.DifferenceIconContainer>
                {Math.abs(Math.round(timeframeDifference * 10) / 10)
                  .toString()
                  .replace('.', numberFormat?.decimal ?? '.')}
                %
              </Styled.Difference>
            )}
          </Styled.Progress>
        </Styled.Body>
      )}

      {isLoading ? (
        <Styled.LoadingFooter />
      ) : (
        <Styled.Footer $isFrozen={!!goal.frozenAt}>
          <TimeProgressChart
            data={entries}
            startDate={goal.startDate}
            endDate={goal.deadline}
            chartStartDate={startDate}
            chartEndDate={endDate}
            startValue={startValue}
            endValue={target}
            weekStartsOn={weekStartsOn}
            isComplete={(completionPercentage ?? 0) >= 100}
            isEmpty={isEmpty}
            fillHeight
            showTodayLine
            numberFormat={numberFormat}
          />
        </Styled.Footer>
      )}
    </Styled.Container>
  );
};
